import { photoSource } from './28-image-sources.mjs';
/** Reference previews contain only the current server-authorized original projection. */
export function sharedPostPreview(ctx, record, { thumbnail = true, compact = false } = {}) {
  if (!record.sourcePostId) return null;
  const source = record.sharedPost;
  if (!source) {
    const unavailable = ctx.e(
      'p',
      'shared-post-unavailable',
      ctx.uiText('Original post unavailable'),
    );
    unavailable.setAttribute('role', 'note');
    return unavailable;
  }
  const preview = ctx.link('', `#/post/${source.id}`, 'shared-post-preview');
  if (compact) preview.classList.add('shared-post-credit');
  preview.setAttribute(
    'aria-label',
    ctx.translate?.('ui.share.openOriginal', { name: source.authorName }) ??
      `Open original post by ${source.authorName}`,
  );
  if (thumbnail && source.image) {
    const image = ctx.e('img', 'shared-post-thumbnail');
    image.src = photoSource(source.image, 640);
    image.alt = source.alt || '';
    image.loading = 'lazy';
    image.width = 72;
    image.height = 72;
    preview.append(image);
  }
  const summary = ctx.append(
    ctx.e('span', 'shared-post-summary'),
    ctx.e('strong', '', source.authorName),
  );
  if (!compact)
    summary.append(
      ctx.e('span', '', source.title || source.text || ctx.uiText('View original post')),
    );
  preview.append(summary);
  return preview;
}

/** Sharing uses explicit confirmation and stable retry identities, never automatic sends. */
export async function openPostSharing(ctx, post) {
  const openingActor = ctx.state?.me.id,
    openingRoute = ctx.route().join('/');
  try {
    if (ctx.ensureFullState && !(await ctx.ensureFullState())) return;
    if (ctx.state?.me.id !== openingActor || ctx.route().join('/') !== openingRoute) return;
  } catch (error) {
    ctx.toast(error.message);
    return;
  }
  if (ctx.ensureFullState) {
    const current = ctx.state.posts.find((entry) => entry.id === post.id);
    if (!current) {
      ctx.toast('This post is unavailable, private, or has been removed.');
      return;
    }
    post = current;
  }
  const actor = ctx.state.me.id;
  const route = ctx.route().join('/');
  const originalId = post.sourcePostId || post.id;
  const root = ctx.e('div', 'post-sharing');
  const choices = ctx.e('div', 'share-choices');
  const detail = ctx.e('div', 'share-detail');
  const status = ctx.e('p', 'share-status');
  status.setAttribute('role', 'status');
  const active = () =>
    ctx.state.me.id === actor && ctx.route().join('/') === route && root.isConnected;
  const enabled = (key) =>
    ctx.featureEnabled
      ? ctx.featureEnabled(key)
      : !ctx.state.features || ctx.state.features[key] === true;
  const sourceAvailable = !post.sourcePostId || !!post.sharedPost;
  const own = ctx.state.posts?.find(
    (p) => p.kind === 'repost' && p.authorId === actor && p.sourcePostId === originalId,
  );
  root.append(choices, detail, status);
  const url = `${location.origin}/#/post/${originalId}`;
  function fail(error) {
    if (active()) status.textContent = error.message || ctx.uiText('Could not share. Try again.');
  }
  function form(type) {
    detail.replaceChildren();
    status.textContent = '';
    const note = ctx.field('textarea', {
      placeholder: ctx.uiText('Add a note (optional)'),
      ariaLabel: ctx.uiText('Sharing note'),
      maxLength: 500,
    });
    const selected = new Set();
    let audience = 'friends',
      previousPayload,
      requestId;
    const send = ctx.button(
      type === 'repost' ? 'Repost' : 'Send to selected friends',
      async () => {
        if (!active()) return;
        const payload = { type, id: post.id, text: note.value };
        if (type === 'repost') payload.audience = audience;
        else payload.memberIds = [...selected].sort();
        if (!enabled(type === 'repost' ? 'publishing' : 'messages')) {
          status.textContent = ctx.uiText('Sharing is currently unavailable.');
          return;
        }
        if (type === 'share-post' && !selected.size) {
          status.textContent = ctx.uiText('Choose a friend first.');
          return;
        }
        const serialized = JSON.stringify(payload);
        if (serialized !== previousPayload) {
          requestId = crypto.randomUUID();
          previousPayload = serialized;
        }
        send.disabled = true;
        status.textContent = ctx.uiText('Sharing…');
        try {
          await ctx.command({ ...payload, requestId }, { paint: false });
          if (!active()) return;
          detail.replaceChildren();
          status.textContent = ctx.uiText(
            type === 'repost' ? 'Reposted.' : 'Sent to selected friends.',
          );
          if (type === 'repost') detail.append(undoButton());
        } catch (error) {
          fail(error);
        } finally {
          if (active()) send.disabled = false;
        }
      },
      'primary-button',
    );
    if (type === 'repost') {
      const group = ctx.e('div', 'share-audience');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', ctx.uiText('Repost audience'));
      for (const [value, label] of [
        ['friends', 'Friends'],
        ['public', 'Public'],
      ]) {
        const button = ctx.button(label, () => {
          audience = value;
          for (const child of group.children)
            child.setAttribute('aria-pressed', String(child === button));
        });
        button.setAttribute('aria-pressed', String(value === audience));
        group.append(button);
      }
      detail.append(group);
    } else {
      const search = ctx.field('input', {
        placeholder: ctx.uiText('Search friends'),
        ariaLabel: ctx.uiText('Search friends'),
      });
      const friends = ctx.e('div', 'share-friends');
      function render() {
        friends.replaceChildren();
        const query = search.value.trim().toLocaleLowerCase();
        const matches = ctx.state.friends
          .filter((id) => !ctx.state.settings?.blocked?.includes(id))
          .map((id) => ctx.who(id))
          .filter(
            (user) =>
              user && `${user.name} ${user.handle || ''}`.toLocaleLowerCase().includes(query),
          );
        for (const user of matches) {
          const button = ctx.button(user.name, () => {
            if (selected.has(user.id)) selected.delete(user.id);
            else if (selected.size < 8) selected.add(user.id);
            else {
              status.textContent = ctx.uiText('Choose up to eight friends.');
              return;
            }
            button.setAttribute('aria-pressed', String(selected.has(user.id)));
            send.textContent =
              ctx.translate?.('ui.share.sendCount', { count: selected.size }) ??
              `Send to selected friends (${selected.size})`;
          });
          button.classList.add('share-friend-tile');
          if (ctx.avatar) button.replaceChildren(ctx.avatar(user), ctx.e('span', '', user.name));
          button.setAttribute('aria-pressed', String(selected.has(user.id)));
          friends.append(button);
        }
        if (!matches.length)
          friends.append(ctx.e('p', 'subtle', ctx.uiText('No connected friends match.')));
      }
      search.addEventListener('input', render);
      render();
      detail.append(search, friends);
    }
    detail.append(note, send);
  }
  function undoButton() {
    const requestId = crypto.randomUUID();
    const undo = ctx.button('Undo repost', async () => {
      if (!active()) return;
      undo.disabled = true;
      try {
        await ctx.command({ type: 'undo-repost', id: originalId, requestId }, { paint: false });
        if (active()) {
          detail.replaceChildren();
          status.textContent = ctx.uiText('Repost removed.');
        }
      } catch (error) {
        fail(error);
      } finally {
        if (active()) undo.disabled = false;
      }
    });
    return undo;
  }
  if (own) choices.append(undoButton());
  else if (
    sourceAvailable &&
    enabled('publishing') &&
    (post.sharedPost ? post.sharedPost.audience : post.audience) === 'public'
  )
    choices.append(ctx.button('Repost', () => form('repost')));
  if (sourceAvailable && enabled('messages'))
    choices.append(ctx.button('Send to friends', () => form('share-post')));
  if (navigator.share)
    choices.append(
      ctx.button('Share link', async () => {
        if (!active()) return;
        try {
          await navigator.share({ title: 'A post on myAudience', url });
        } catch (error) {
          if (error.name !== 'AbortError') fail(error);
        }
      }),
    );
  choices.append(
    ctx.button('Copy link', async () => {
      if (!active()) return;
      try {
        await navigator.clipboard.writeText(url);
        if (active())
          status.textContent = ctx.uiText('Link copied. The original audience still applies.');
      } catch {
        if (active())
          detail.replaceChildren(
            ctx.field('input', { value: url, readOnly: true, ariaLabel: ctx.uiText('Post link') }),
          );
      }
    }),
  );
  root.append(
    ctx.e(
      'p',
      'subtle',
      ctx.uiText('Sharing keeps the original audience. A post may become unavailable.'),
    ),
  );
  ctx.dialog('Share post', root);
}
